// src/components/ChatModal.js

import React, { useState, useRef, useEffect } from 'react';
import Modal from 'react-modal';
import { supabase } from '../../supabase';
import toast from 'react-hot-toast';
import { FaPaperclip, FaTimesCircle, FaFilePdf } from 'react-icons/fa';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import './ChatModal.css';

// Configure marked options
marked.setOptions({
  breaks: true,
  gfm: true
});

Modal.setAppElement('#root');

export const ChatModal = ({ isOpen, onRequestClose, file, onAttachFileClick, onRemoveFileClick }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const chatEndRef = useRef(null);
  const prevFileIdRef = useRef(null);

  const handleClearChat = () => {
    if (window.confirm("Are you sure you want to clear this conversation history?")) {
      const key = file ? `chat_history_${file.id}` : 'chat_history_general';
      localStorage.removeItem(key);
      const welcomeText = file
        ? `Hi! Ask me anything about "${file.fileName}".`
        : `Hi! I'm Genie, your AI assistant. How can I help you today?`;
      const initial = [{ sender: 'ai', text: welcomeText }];
      setMessages(initial);
      localStorage.setItem(key, JSON.stringify(initial));
      toast.success("Chat history cleared!");
    }
  };

  // Auto-scroll to the bottom of the viewport
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('body-scroll-locked');
    } else {
      document.body.classList.remove('body-scroll-locked');
    }
    return () => {
      document.body.classList.remove('body-scroll-locked');
    };
  }, [isOpen]);

  // Load conversation thread from cache (localStorage) when modal is opened or file context changes
  useEffect(() => {
    if (isOpen) {
      const key = file ? `chat_history_${file.id}` : 'chat_history_general';
      const cached = localStorage.getItem(key);

      if (cached) {
        setMessages(JSON.parse(cached));
      } else {
        const welcomeText = file
          ? `Hi! Ask me anything about "${file.fileName}".`
          : `Hi! I'm Genie, your AI assistant. How can I help you today?`;

        const initial = [{ sender: 'ai', text: welcomeText }];
        setMessages(initial);
        localStorage.setItem(key, JSON.stringify(initial));
      }
      setInput('');
      prevFileIdRef.current = file ? file.id : null;
    }
  }, [isOpen, file]);

  // Handle smart dynamic file context update announcements
  useEffect(() => {
    if (isOpen && messages.length > 0) {
      const currentFileId = file ? file.id : null;
      if (prevFileIdRef.current !== currentFileId) {
        const contextUpdateText = file
          ? `Context updated. Now asking about "${file.fileName}".`
          : `File context removed. Now in general chat mode.`;

        const systemMessage = { sender: 'system', text: contextUpdateText };
        setMessages(prev => {
          const updated = [...prev, systemMessage];
          const key = file ? `chat_history_${file.id}` : 'chat_history_general';
          localStorage.setItem(key, JSON.stringify(updated));
          return updated;
        });
        prevFileIdRef.current = currentFileId;
      }
    }
  }, [file, isOpen, messages.length]);

  const streamingRef = useRef(false);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = { sender: 'user', text: input };
    const currentKey = file ? `chat_history_${file.id}` : 'chat_history_general';

    setMessages(prev => {
      const updated = [...prev, userMessage];
      localStorage.setItem(currentKey, JSON.stringify(updated));
      return updated;
    });

    const originalInput = input;
    setInput('');
    setIsLoading(true);
    streamingRef.current = true;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Authentication error.");

      const functionUrl = `/api/chatWithFile`;
      const fileUrl = file ? file.fileUrl : null;
      if (file && !fileUrl) throw new Error("The selected file is missing a valid URL.");

      // Grab existing conversation context mapped strictly for model
      const conversationHistory = messages
        .filter(msg => msg.sender !== 'system')
        .map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'model',
          parts: [{ text: msg.text }]
        }));

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({ data: { fileUrl, question: originalInput, history: conversationHistory } }),
      });

      if (!response.ok) {
        const errorResult = await response.json().catch(() => ({}));
        throw new Error(errorResult.error || "Server error.");
      }

      const result = await response.json();
      const fullText = result.data.answer;

      // Add a placeholder message for the typewriter effect
      setMessages(prev => [...prev, { sender: 'ai', text: '' }]);

      // Typewriter streaming effect — reveal words progressively
      const words = fullText.split(/(\s+)/); // split but keep whitespace
      let displayed = '';
      for (let i = 0; i < words.length; i++) {
        if (!streamingRef.current) break; // allow abort on unmount
        displayed += words[i];
        const snapshot = displayed;
        setMessages(prev => {
          const updated = [...prev];
          if (updated.length > 0 && updated[updated.length - 1].sender === 'ai') {
            updated[updated.length - 1] = { ...updated[updated.length - 1], text: snapshot };
          }
          return updated;
        });
        // Small delay between words for natural streaming feel
        if (words[i].trim()) {
          await new Promise(r => setTimeout(r, 45));
        }
      }

      // Save final completed thread to localStorage
      setMessages(prev => {
        // Ensure the final message has the complete text
        const updated = [...prev];
        if (updated.length > 0 && updated[updated.length - 1].sender === 'ai') {
          updated[updated.length - 1] = { ...updated[updated.length - 1], text: fullText };
        }
        localStorage.setItem(currentKey, JSON.stringify(updated));
        return updated;
      });

    } catch (error) {
      toast.error(`Chat Error: ${error.message}`);
      const errorMessage = { sender: 'ai', text: `Sorry, I ran into a problem.` };
      setMessages(prev => {
        const updated = [...prev];
        // Remove the empty/incomplete AI placeholder if it exists
        if (updated.length > 0 && updated[updated.length - 1].sender === 'ai' && !updated[updated.length - 1].text) {
          updated.pop();
        }
        const final = [...updated, errorMessage];
        localStorage.setItem(currentKey, JSON.stringify(final));
        return final;
      });
    } finally {
      setIsLoading(false);
      streamingRef.current = false;
    }
  };

  // Safe math and markdown compilation
  const escapeHtml = (unsafe) => {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const renderFormattedContent = (text, isInline = false) => {
    if (!text) return '';

    const mathBlocks = [];
    const mathInlines = [];

    let processed = text;

    // Extract block equations
    processed = processed.replace(/\$\$\s*([\s\S]*?)\s*\$\$/g, (match, formula) => {
      const placeholder = `MATHBLOCKN${mathBlocks.length}`;
      mathBlocks.push(formula);
      return placeholder;
    });

    // Extract inline equations
    processed = processed.replace(/\$(.*?)\$/g, (match, formula) => {
      const placeholder = `MATHINLINEN${mathInlines.length}`;
      mathInlines.push(formula);
      return placeholder;
    });

    // Parse Markdown using marked
    let html = isInline ? marked.parseInline(processed) : marked.parse(processed);
    
    // Sanitize generated HTML to prevent XSS (allowing standard formatting but blocking scripts/malicious handlers)
    html = DOMPurify.sanitize(html);

    // Restore math blocks
    mathBlocks.forEach((formula, idx) => {
      const placeholder = `MATHBLOCKN${idx}`;
      const mathHtml = `<span class="math-block">${escapeHtml(formula)}</span>`;
      html = html.replace(placeholder, mathHtml);
    });

    // Restore math inlines
    mathInlines.forEach((formula, idx) => {
      const placeholder = `MATHINLINEN${idx}`;
      const mathHtml = `<span class="math-inline">${escapeHtml(formula)}</span>`;
      html = html.replace(placeholder, mathHtml);
    });

    return html;
  };

  const renderMessageText = (text, sender) => {
    if (sender === 'system') return text;
    if (!text) return '';

    try {
      const parsedHtml = renderFormattedContent(text);
      return <span className="markdown-body" dangerouslySetInnerHTML={{ __html: parsedHtml }} />;
    } catch (e) {
      console.error("Math & Marked parsing error:", e);
      return <span>{text}</span>;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Chat Modal"
      className="chat-modal-content"
      overlayClassName="overlay"
    >
      <div className="chat-modal-header">
        <h2 title="Chat with Genie">
          Chat with Genie
        </h2>
        <div className="chat-header-actions">
          {messages.length > 1 && (
            <button onClick={handleClearChat} className="chat-clear-btn" title="Clear Chat History">
              Clear Chat
            </button>
          )}
          <button className="modal-close-btn" onClick={onRequestClose} aria-label="Close Chat">&times;</button>
        </div>
      </div>

      <div className="chat-messages-viewport">
        {messages.map((msg, index) => (
          <div key={index} className={`chat-bubble ${msg.sender}`}>
            {renderMessageText(msg.text, msg.sender)}
          </div>
        ))}
        {isLoading && (messages.length === 0 || messages[messages.length - 1].sender !== 'ai') && (
          <div className="chat-typing-bubble">
            <div className="typing-dot" />
            <div className="typing-dot" />
            <div className="typing-dot" />
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      <div className="chat-attachment-bar">
        {file ? (
          <div className="chat-attached-file">
            <FaFilePdf style={{ color: 'var(--color-danger)' }} />
            <span>Context: <strong>{file.fileName}</strong></span>
            <button className="chat-remove-attach" onClick={onRemoveFileClick} title="Remove file context">
              <FaTimesCircle />
            </button>
          </div>
        ) : (
          <button className="chat-attach-btn" onClick={onAttachFileClick}>
            <FaPaperclip />
            Attach a file for context
          </button>
        )}
      </div>

      <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: '10px' }}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask Genie a question..."
          style={{ flex: 1, padding: '12px 16px', borderRadius: '20px', border: '1px solid var(--color-border)', fontSize: '14px', backgroundColor: 'var(--color-background)', color: 'var(--color-text-primary)' }}
          disabled={isLoading}
        />
        <button type="submit" className="chat-send-btn" style={{ borderRadius: '20px', padding: '0 24px' }} disabled={isLoading}>Send</button>
      </form>
    </Modal>
  );
};

// End of ChatModal component